import isEqual from "lodash/isEqual";
import moment, { Moment } from "moment";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";

import Alert from "veneer/Alert";

import {
  DATE_RANGE_MAX_DAYS,
  SEVERITY_OPTIONS,
} from "cortex/ui/src/fleet/constant/customEvents";
import type { Application } from "cortex/ui/src/fleet/view/application/model";
import type { Device } from "cortex/ui/src/fleet/view/device/model";
import { useAppSelector } from "cortex/ui/src/hooks";
import { getAuthUser } from "cortex/ui/src/redux-store/auth/selectors";
import { TableDataSpinner } from "cortex/ui/src/ui/loader";
import Notification from "cortex/ui/src/ui/notification";
import { formatApplicationSource } from "cortex/ui/src/ui/table/formats";
import type { Option } from "cortex/ui/src/ui/table/types";
import { getTargetOptions as buildTargetOptions } from "cortex/ui/src/util/getTargetOptions";
import {
  type ResponseLike,
  getErrorMessage,
} from "cortex/ui/src/util/response";
import { sortByLabelAsc } from "cortex/ui/src/util/sortByProp";

import EventTable from "./EventTable";
import {
  EventFilterOptions,
  EventTableFilterState,
  createDefaultEventDateRange,
  usePersistedEventTableFilters,
} from "./EventTableFilters";
import type { Event } from "../model";

export const HARD_LIMIT_EVENTS_TABLE = 400000;

interface EventsTableProps {
  applications: Record<string, Application>;
  device?: Device;
  devices: Record<string, Device>;
  events: Event[];
  fetchMethod: (args: FetchEventsArgs) => Promise<FetchEventsResponse>;
  hardLimit?: number;
}

interface FetchEventsArgs {
  deviceId?: string;
  end?: string;
  filters: Record<string, string[]>;
  start?: string;
}

interface FetchEventsResponse {
  data: {
    data: Event[];
  };
}

interface OptionsMap {
  eventNameOptions: Record<string, string>;
  sourceOptions: Record<string, string>;
}

function getFilterOptions(
  optionsMap: Record<string, string>,
): Array<Option<string>> {
  return Object.entries(optionsMap)
    .map(([value, label]) => ({ label, value }))
    .sort(sortByLabelAsc);
}

function getTargetFilterOptions(devices: Record<string, Device>) {
  const targetOptions = buildTargetOptions(devices);

  return {
    deviceOptions: targetOptions
      .filter(({ value }) => value.startsWith("d_"))
      .map((option) => ({
        ...option,
        label: option.label.replace(/^device: /, ""),
      })),
    tagOptions: targetOptions
      .filter(({ value }) => value.startsWith("t_"))
      .map((option) => ({
        ...option,
        label: option.label.replace(/^tag: /, ""),
      })),
  };
}

export default function EventsTableContainer({
  applications,
  device,
  devices,
  events,
  fetchMethod,
  hardLimit = HARD_LIMIT_EVENTS_TABLE,
}: EventsTableProps) {
  const user = useAppSelector(getAuthUser);
  const tableSettingsKey = device
    ? `device-events_acct_${user?.account_id ?? 0}`
    : `events_acct_${user?.account_id ?? 0}`;
  const { filters, setFilters } = usePersistedEventTableFilters(
    `cortex_table_${tableSettingsKey}`,
  );
  const optionsMapRef = useRef<OptionsMap>({
    eventNameOptions: {},
    sourceOptions: {},
  });
  const prevApplicationsRef = useRef(applications);
  const prevDevicesRef = useRef(devices);
  const prevEventsRef = useRef(events);
  const isMountedRef = useRef(false);
  const fetchRequestIdRef = useRef(0);
  const lastRequestedFiltersRef = useRef<EventTableFilterState | null>(null);
  const requestImmediatelyRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const [errorMessage, setErrorMessage] = useState("");
  const [isFilterInteractionActive, setIsFilterInteractionActive] =
    useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [filterOptions, setFilterOptions] = useState<EventFilterOptions>({
    ...getTargetFilterOptions(devices),
    eventNameOptions: [],
    priorityOptions: SEVERITY_OPTIONS,
    sourceOptions: [],
  });

  const getAllFilterOptions = useCallback(
    (eventList: ReadonlyArray<Event>): EventFilterOptions => {
      const { eventNameOptions, sourceOptions } = optionsMapRef.current;

      eventList.forEach((event) => {
        eventNameOptions[event.name] = event.name;
        sourceOptions[String(event.source)] = formatApplicationSource(
          applications,
          event.source,
        );
      });

      return {
        ...getTargetFilterOptions(devices),
        eventNameOptions: getFilterOptions(eventNameOptions),
        priorityOptions: SEVERITY_OPTIONS,
        sourceOptions: getFilterOptions(sourceOptions),
      };
    },
    [applications, devices, formatApplicationSource],
  );

  useEffect(() => {
    setFilterOptions(getAllFilterOptions([]));
    setIsLoading(true);
  }, [getAllFilterOptions]);

  useEffect(() => {
    const applicationsChanged = !isEqual(
      prevApplicationsRef.current,
      applications,
    );
    const devicesChanged = !isEqual(prevDevicesRef.current, devices);
    const eventsChanged = !isEqual(prevEventsRef.current, events);

    if (applicationsChanged || devicesChanged || eventsChanged) {
      setFilterOptions(getAllFilterOptions(events));
    }

    prevApplicationsRef.current = applications;
    prevDevicesRef.current = devices;
    prevEventsRef.current = events;
  }, [applications, devices, events, getAllFilterOptions]);

  const validateFilters = useCallback(
    ([startDate, endDate]: EventTableFilterState["eventDate"]): boolean => {
      if (!startDate || !endDate) {
        setErrorMessage("");
        return false;
      }

      if (endDate.isBefore(startDate)) {
        setErrorMessage("Error: End must be after start for date range.");
        return false;
      }

      const days = endDate.diff(startDate, "days");

      if (days > DATE_RANGE_MAX_DAYS) {
        setErrorMessage(
          `Error: date range must be less than ${DATE_RANGE_MAX_DAYS} days.`,
        );
        return false;
      }

      setErrorMessage("");
      return true;
    },
    [],
  );

  const handleFilterInteractionChange = useCallback(
    (isInteracting: boolean) => {
      setIsFilterInteractionActive(isInteracting);

      if (!isInteracting) {
        setFilters((previous) => {
          const [startDate, endDate] = previous.eventDate;
          if (startDate && endDate) {
            return previous;
          }

          return {
            ...previous,
            eventDate: createDefaultEventDateRange(),
            eventDatePreset: "lastHour",
          };
        });
      }
    },
    [setFilters],
  );

  const goFetchEvents = useCallback(
    async (serviceArgs: FetchEventsArgs) => {
      const requestId = ++fetchRequestIdRef.current;

      try {
        const response = await fetchMethod(serviceArgs);

        if (!isMountedRef.current || requestId !== fetchRequestIdRef.current) {
          return;
        }

        setErrorMessage("");
        setFilterOptions(getAllFilterOptions(response.data.data));
      } catch (error: unknown) {
        if (!isMountedRef.current || requestId !== fetchRequestIdRef.current) {
          return;
        }

        setErrorMessage(
          `Failed to retrieve events: ${getErrorMessage(
            error as Error | ResponseLike | null,
          )}`,
        );
      } finally {
        if (isMountedRef.current && requestId === fetchRequestIdRef.current) {
          setIsLoading(false);
        }
      }
    },
    [fetchMethod, getAllFilterOptions],
  );

  const fetchEvents = useCallback(
    (
      filters: Record<string, string[]> = {},
      dates: { endDate?: Moment | null; startDate?: Moment | null } = {},
    ) => {
      const deviceId = device?.device_id;
      const end = dates.endDate?.clone().utc().format("YYYY-MM-DDTHH:mm:ss");
      const start = dates.startDate
        ?.clone()
        .utc()
        .format("YYYY-MM-DDTHH:mm:ss");

      const serviceArgs: FetchEventsArgs = {
        deviceId,
        end,
        filters,
        start,
      };

      setIsLoading(true);
      void goFetchEvents(serviceArgs);
    },
    [device, goFetchEvents],
  );

  useEffect(() => {
    if (!validateFilters(filters.eventDate)) {
      return;
    }

    if (lastRequestedFiltersRef.current === filters) {
      return;
    }

    if (isFilterInteractionActive) {
      return;
    }

    const apiFilters: Record<string, string[]> = {};
    if (filters.name.length > 0) {
      apiFilters.name = [...filters.name];
    }
    if (filters.priority.length > 0) {
      apiFilters.priority = [...filters.priority];
    }
    if (filters.source.length > 0) {
      apiFilters.source = filters.source.map((value) => String(Number(value)));
    }
    if (!device && filters.targets.length > 0) {
      apiFilters.targets = [...filters.targets];
    }

    const requestEvents = () => {
      lastRequestedFiltersRef.current = filters;
      fetchEvents(apiFilters, {
        endDate: filters.eventDate[1],
        startDate: filters.eventDate[0],
      });
    };

    if (!lastRequestedFiltersRef.current || requestImmediatelyRef.current) {
      requestImmediatelyRef.current = false;
      requestEvents();
      return;
    }

    // Debounce the request to avoid making too many API calls in quick succession.
    const timeoutId = window.setTimeout(requestEvents, 2000);

    return () => window.clearTimeout(timeoutId);
  }, [
    device,
    fetchEvents,
    filters,
    isFilterInteractionActive,
    validateFilters,
  ]);

  const setEndDateToOldest = useCallback(
    (oldest: Moment) => {
      requestImmediatelyRef.current = true;
      setFilters((previous) => ({
        ...previous,
        eventDate: [oldest.clone().subtract(1, "hour"), oldest],
        eventDatePreset: null,
      }));
    },
    [setFilters],
  );

  const eventData = isLoading ? [] : events;

  const noDataText = isLoading ? (
    <TableDataSpinner title="Fetching events…" />
  ) : (
    "No events to display."
  );

  const oldest = useMemo(() => {
    if (events.length === 0) {
      return null;
    }

    return moment
      .min(events.map((event) => moment.utc(event.event_date)))
      .local();
  }, [events]);

  return (
    <>
      <Notification error={errorMessage} />
      {!isLoading && events.length === hardLimit && oldest && (
        <Alert severity="warning">
          Showing the {hardLimit.toLocaleString()} most recent events in your
          search. The oldest event shown is from{" "}
          {oldest.format("MMM Do, YYYY hh:mm:ss a")}.{" "}
          <Link
            onClick={(event) => {
              event.preventDefault();
              setEndDateToOldest(oldest);
            }}
            to="#"
          >
            See older events
          </Link>
          .
        </Alert>
      )}
      <EventTable
        applications={applications}
        device={device}
        devices={devices}
        emptyState={noDataText}
        events={eventData}
        filterOptions={filterOptions}
        filters={filters}
        onFilterInteractionChange={handleFilterInteractionChange}
        onFiltersChange={setFilters}
        tableSettingsKey={tableSettingsKey}
      />
    </>
  );
}
